/**
 * Strings shared by the landing page and every game screen. English is the source shape: the other
 * languages must have every key (checked by the compiler). Plural keys carry all the forms any of
 * our languages needs (`_one`, `_few`, `_many`, `_other`); English and Armenian use one/other.
 * Never use the brick brand's name in any language.
 */
const en = {
  landing: {
    // The headline is "BrickCity" on its own line, then this.
    headlineRest: "is on the way!",
    playWhileYouWait: "Play while you wait",
    gamesTitle: "Brick games",
    gamesLede: "Small games made of bricks. Pick one and play.",
    newBadge: "New",
    best: "Best: {{score}}",
    play: "Play {{name}}",
    language: "Language",
  },
  games: {
    drop: {
      name: "Brick Drop",
      pitch: "Rotate and drop brick pieces. Fill a whole row to clear it — the faster you clear, the faster they fall.",
      chip: "Keyboard or touch",
    },
    stack: {
      name: "Skyline Stacker",
      pitch: "Drop each brick on the tower — whatever hangs over the edge breaks off. How tall can you build?",
      chip: "One tap to play",
    },
    breaker: {
      name: "Brick Breaker",
      pitch: "Bounce the ball off your paddle and smash the wall. Black bricks take two hits; golden ones score big.",
      chip: "Mouse, keys or touch",
    },
  },
  frame: {
    backHome: "Back to BrickCity",
    pause: "Pause",
    restart: "Restart",
    resume: "Resume",
    paused: "Paused",
    howToPlay: "How to play",
    gameOver: "Game over",
    newBest: "New best!",
    playAgain: "Play again",
    score: "Score",
    best: "Best",
    level: "Level",
    docTitle: "{{name}} — BrickCity",
  },
  keys: {
    space: "Space",
    click: "Click",
    mouse: "Mouse",
    or: "or",
  },
  error: {
    title: "Couldn't start the game.",
    offline: "Check your connection and reload the page.",
    unsupported: "This browser can't draw the game. Try a newer browser.",
  },
};

export type CommonStrings = typeof en;

const hy: CommonStrings = {
  landing: {
    headlineRest: "ճանապարհին է!",
    playWhileYouWait: "Խաղա, մինչ սպասում ես",
    gamesTitle: "Աղյուսե խաղեր",
    gamesLede: "Փոքրիկ խաղեր աղյուսներից։ Ընտրիր մեկը և խաղա։",
    newBadge: "Նոր",
    best: "Ռեկորդ՝ {{score}}",
    play: "Խաղալ «{{name}}»",
    language: "Լեզու",
  },
  games: {
    drop: {
      name: "Աղյուսե անձրև",
      pitch: "Պտտիր ու գցիր աղյուսե պատկերները։ Լցրու ամբողջ շարքը, և այն կանհետանա․ որքան արագ մաքրես, այնքան արագ կընկնեն։",
      chip: "Ստեղնաշար կամ հպում",
    },
    stack: {
      name: "Երկնաքեր",
      pitch: "Գցիր ամեն աղյուսը աշտարակի վրա․ եզրից դուրս մնացածը կպոկվի։ Որքա՞ն բարձր կկառուցես։",
      chip: "Մեկ հպումով",
    },
    breaker: {
      name: "Քանդիր պատը",
      pitch: "Գնդակը ետ մղիր թիակով և քանդիր պատը։ Սև աղյուսներին երկու հարված է պետք, ոսկեգույնները շատ միավոր են տալիս։",
      chip: "Մկնիկ, ստեղներ կամ հպում",
    },
  },
  frame: {
    backHome: "Վերադառնալ BrickCity",
    pause: "Դադար",
    restart: "Սկսել նորից",
    resume: "Շարունակել",
    paused: "Դադար",
    howToPlay: "Ինչպես խաղալ",
    gameOver: "Խաղն ավարտվեց",
    newBest: "Նոր ռեկորդ!",
    playAgain: "Խաղալ նորից",
    score: "Միավոր",
    best: "Ռեկորդ",
    level: "Փուլ",
    docTitle: "{{name}} — BrickCity",
  },
  keys: {
    space: "Բացատ",
    click: "Սեղմում",
    mouse: "Մկնիկ",
    or: "կամ",
  },
  error: {
    title: "Չհաջողվեց սկսել խաղը։",
    offline: "Ստուգիր ինտերնետ կապը և թարմացրու էջը։",
    unsupported: "Այս բրաուզերը չի կարող նկարել խաղը։ Փորձիր ավելի նոր բրաուզեր։",
  },
};

const ru: CommonStrings = {
  landing: {
    headlineRest: "уже в пути!",
    playWhileYouWait: "Поиграй, пока ждёшь",
    gamesTitle: "Игры из кирпичиков",
    gamesLede: "Маленькие игры из кирпичиков. Выбирай и играй.",
    newBadge: "Новое",
    best: "Рекорд: {{score}}",
    play: "Играть в «{{name}}»",
    language: "Язык",
  },
  games: {
    drop: {
      name: "Кирпичный дождь",
      pitch: "Поворачивай и роняй фигуры из кирпичиков. Заполни ряд целиком, и он исчезнет — чем быстрее очищаешь, тем быстрее они падают.",
      chip: "Клавиатура или касание",
    },
    stack: {
      name: "Небоскрёб",
      pitch: "Роняй каждый кирпичик на башню — всё, что свисает с края, отваливается. Насколько высоко построишь?",
      chip: "Одно касание",
    },
    breaker: {
      name: "Разбей стену",
      pitch: "Отбивай мяч платформой и разбивай стену. Чёрные кирпичи — с двух ударов, золотые приносят много очков.",
      chip: "Мышь, клавиши или касание",
    },
  },
  frame: {
    backHome: "Вернуться в BrickCity",
    pause: "Пауза",
    restart: "Заново",
    resume: "Продолжить",
    paused: "Пауза",
    howToPlay: "Как играть",
    gameOver: "Игра окончена",
    newBest: "Новый рекорд!",
    playAgain: "Играть снова",
    score: "Очки",
    best: "Рекорд",
    level: "Уровень",
    docTitle: "{{name}} — BrickCity",
  },
  keys: {
    space: "Пробел",
    click: "Клик",
    mouse: "Мышь",
    or: "или",
  },
  error: {
    title: "Не удалось запустить игру.",
    offline: "Проверь подключение к интернету и обнови страницу.",
    unsupported: "Этот браузер не умеет рисовать игру. Попробуй браузер поновее.",
  },
};

const common = { hy, en, ru };
export default common;
